import { connect } from 'react-redux';
import { get, isEmpty, keyBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// Utils
import { convertSPPQuantityInMasterUnit } from '@orders/utils/computePackagingToSupplierProduct';
import {
  formatSPAndRecipesPayloadForCreation,
  formatSPAndRecipesPayloadForDeliveryNote,
} from '../../utils/formatPayload';
import {
  TRANSFER_DETAILS_VIEW,
  TRANSFER_STATUS,
} from '@stocks/StocksInventories/Transfers/utils/constants';
import transferUtils from '../../utils/index';

// Hooks
import { useDatePickerState } from '@hooks/useDatePickerState';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Selectors
import { canReceiveTransferV2, canSendTransfer } from '@selectors/actions/transferActions';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getSalesPointStores } from '@selectors/stores';

// Services
import storeService from '@services/store';
import transferListService from '@services/transferList';
import transfersService from '@services/transfers';

// Components
import { getSendTransferListModalConfig } from '@stocks/StocksInventories/Transfers/utils/transferModals';

import { PageContainer } from './styledComponents';
import TransferContent from '../TransferContent';
import TransfersHeader from '../Header';

export const TransfersDetails = (props) => {
  const {
    pageLoading,
    pageLoaded,
    showErrorMessage,
    showSuccessMessage,
    match,
    user,
    history,
    stores,
    openGenericModal,
    refreshGenericModal,
    authorizedActions,
  } = props;

  const [transferList, setTransferList] = useState({});
  const [allStoresOfClient, setAllStoresOfClient] = useState([]);
  const [emailsTo, setEmailsTo] = useState([]);
  const [emailsCC, setEmailsCC] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [supplierProductsToDisplay, setSupplierProductsToDisplay] = useState([]);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [recipesToDisplay, setRecipesToDisplay] = useState([]);
  const [transferDetailsView, setTransferDetailsView] = useState(TRANSFER_DETAILS_VIEW.DRAFT);
  const [enableDatePicker, setEnableDatePicker] = useState(false);
  const [enableTimePicker, setEnableTimePicker] = useState(false);

  const datePickerState = useDatePickerState(null);

  /* EFFECT */

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const config = getSendTransferListModalConfig({
      transferList,
      emailsTo,
      emailsCC,
      setEmailsTo,
      setEmailsCC,
      handleTransferListSend,
    });

    refreshGenericModal(config);
  }, [emailsTo, emailsCC]);

  /* FONCTIONS */

  const getAvailableSupplierProductsAndRecipes = async (storeId, sentAt) => {
    try {
      const { supplierProducts, recipes } =
        await transfersService.getSupplierProductsAndRecipesByStoreId(storeId, { sentAt });

      const formattedSupplierProducts = supplierProducts.map((supplierProduct) => ({
        ...supplierProduct,
        supplierName: get(supplierProduct, 'lnkSupplierSupplierproductrel.name', null),
        isIngredientStrategic: get(
          supplierProduct,
          'lnkEntitySupplierproductrel.isStrategic',
          false,
        ),
        selectedPackagingId: supplierProduct.packagings[0].id,
        packagings: supplierProduct.packagings.map((packaging) => ({
          ...packaging,
          selectedQuantity: 0,
          quantityInMasterUnit: convertSPPQuantityInMasterUnit(
            packaging.id,
            supplierProduct.packagings,
          ),
        })),
      }));

      const formattedRecipes = recipes.map((recipe) => ({
        ...recipe,
        selectedQuantity: 0,
      }));

      return { supplierProducts: formattedSupplierProducts, recipes: formattedRecipes };
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DETAILS_FETCH_FAILURE'));
      return { supplierProducts: [] };
    }
  };

  const formatSupplierProducts = (
    transferDetailsView,
    supplierProducts,
    availableSupplierProducts,
  ) => {
    const formattedSupplierProducts = supplierProducts.map((supplierProduct) => {
      const sppKeyById = keyBy(supplierProduct.packagings, 'id');

      const spTransferKeyBySPPId = keyBy(supplierProduct.transfers, 'supplierProductPackagingId');

      const masterPackagingIds = supplierProduct.transfers.reduce(
        (acc, { supplierProductPackagingId }) => {
          const packaging = sppKeyById[supplierProductPackagingId];

          if (!packaging) {
            return acc;
          }

          const masterPackagingId = packaging.masterSupplierProductPackagingId || packaging.id;

          acc.add(masterPackagingId);

          return acc;
        },
        new Set(),
      );

      const packagingUsedForTransfer = supplierProduct.packagings.filter(
        ({ id, masterSupplierProductPackagingId }) =>
          // Filter packagings that were used in transfer and shared the same master packaging id
          masterPackagingIds.has(masterSupplierProductPackagingId || id),
      );

      // Master packaging carry the unit but is not necessarily used in stock, so get the unit this way
      const supplierProductUnit =
        packagingUsedForTransfer.find(({ unit }) => unit !== null)?.unit || null;

      const packagingUsedInOrder = packagingUsedForTransfer.find(
        ({ isUsedInOrder }) => isUsedInOrder,
      );

      const formattedPackagings = packagingUsedForTransfer
        .filter(({ isUsedInStock }) => isUsedInStock)
        .map((packaging) => {
          const associatedTransfer = spTransferKeyBySPPId[packaging.id] || {};

          const quantity = transferUtils.getTransferQuantityToDisplay(
            associatedTransfer,
            transferDetailsView,
          );

          return {
            ...packaging,
            selectedQuantity: quantity,
            transferNoteQuantity: associatedTransfer.transferNoteQuantity,
            deliveryNoteQuantity: associatedTransfer.deliveryNoteQuantity,
            receivedQuantity: associatedTransfer.receivedQuantity,
            price: associatedTransfer.price || 0,
            quantityInMasterUnit: convertSPPQuantityInMasterUnit(
              packaging.id,
              supplierProduct.packagings,
            ),
            transferId: associatedTransfer.id || null,
          };
        });

      return {
        ...supplierProduct,
        supplierProductUnit,
        supplierName: get(supplierProduct, 'lnkSupplierSupplierproductrel.name', null),
        isIngredientStrategic: get(
          supplierProduct,
          'lnkEntitySupplierproductrel.isStrategic',
          false,
        ),
        selectedPackagingId: packagingUsedInOrder?.id || get(formattedPackagings, '[0].id', null),
        packagings: formattedPackagings,
      };
    });

    const formattedAvailableSupplierProducts = [...formattedSupplierProducts];

    const existingIds = new Set(formattedSupplierProducts.map(({ id }) => id));

    // Add elements from availableSupplierProductsAndRecipes that don't already exist
    availableSupplierProducts.forEach((supplierProduct) => {
      if (!existingIds.has(supplierProduct.id)) {
        formattedAvailableSupplierProducts.push(supplierProduct);
      }
    });

    return formattedAvailableSupplierProducts;
  };

  const formatRecipes = (transferDetailsView, recipes, availableRecipes) => {
    const formattedRecipes = recipes.map((recipe) => {
      const quantity = transferUtils.getTransferQuantityToDisplay(recipe, transferDetailsView);

      return {
        ...recipe,
        id: recipe.entityId, // here, "recipe.id" is the id of the transfer
        transferId: recipe.id || null,
        selectedQuantity: quantity,
        transferNoteQuantity: recipe.transferNoteQuantity || null,
        deliveryNoteQuantity: recipe.deliveryNoteQuantity || null,
        receivedQuantity: recipe.receivedQuantity || null,
        cost: recipe.price || 0,
      };
    });

    const formattedAvailableRecipes = [...formattedRecipes];

    const existingIds = new Set(formattedRecipes.map(({ entityId }) => entityId));

    availableRecipes.forEach((recipe) => {
      if (!existingIds.has(recipe.id)) {
        formattedAvailableRecipes.push(recipe);
      }
    });

    return formattedAvailableRecipes;
  };

  const fetchData = async () => {
    pageLoading();
    setIsLoading(true);

    try {
      setIsFormDirty(true);

      const transferListId = get(match, 'params.id', null);

      const [
        { transferList: fetchedTransferList, supplierProducts, recipes },
        fetchedClientStores,
      ] = await Promise.all([
        transfersService.getTransferListById(transferListId),
        storeService.getStoresOfClient(),
      ]);

      const formattedStores = fetchedClientStores.map((store) => ({
        ...store,
        transferEmails: store.transferEmails?.split(',').filter((email) => email?.trim()) ?? [],
      }));

      setAllStoresOfClient(formattedStores);
      setTransferList(fetchedTransferList);
      datePickerState.setDate(moment(fetchedTransferList.sentAt));

      const transferDetailsView = getTransferDetailsView(fetchedTransferList, authorizedActions);
      setTransferDetailsView(transferDetailsView);

      // If not in draft, only display SP / Recipes which were specified for the transfer
      if (
        [
          TRANSFER_DETAILS_VIEW.DELIVERY,
          TRANSFER_DETAILS_VIEW.RECEPTION,
          TRANSFER_DETAILS_VIEW.DISABLED,
          TRANSFER_DETAILS_VIEW.SENDER_DISABLED,
        ].includes(transferDetailsView)
      ) {
        const formattedSupplierProducts = formatSupplierProducts(
          transferDetailsView,
          supplierProducts,
          [],
        );
        const formattedRecipes = formatRecipes(transferDetailsView, recipes, []);

        setSupplierProductsToDisplay(formattedSupplierProducts);
        setRecipesToDisplay(formattedRecipes);
        return;
      }

      // Draft mode, get available supplier products and recipes to be displayed
      const { supplierProducts: availableSupplierProducts, recipes: availableRecipes } =
        await getAvailableSupplierProductsAndRecipes(
          fetchedTransferList.senderStoreId,
          fetchedTransferList.sentAt,
        );

      const formattedAvailableSupplierProducts = formatSupplierProducts(
        transferDetailsView,
        supplierProducts,
        availableSupplierProducts,
      );

      const formattedRecipes = formatRecipes(transferDetailsView, recipes, availableRecipes);

      setSupplierProductsToDisplay(formattedAvailableSupplierProducts);
      setRecipesToDisplay(formattedRecipes);
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.DETAILS_FETCH_FAILURE'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  /**
   * Determines which view should be displayed to the current user
   * based on their association with the involved stores, the transfer status, and
   * whether the sender initiated the transfer or not.
   *
   * The returned view type is used to render the correct interface in the transfer details page
   * (e.g., delivery preparation, reception confirmation, draft view, or disabled state).
   *
   * @param {Object} transferList - The transfer record to evaluate.
   * @param {number} transferList.status - The current status of the transfer, as defined in `TRANSFER_STATUS`.
   * @param {boolean} transferList.isSender - Whether the sender store initiated this transfer.
   *
   * @returns {string} One of the `TRANSFER_DETAILS_VIEW` enum values indicating the interface to display to the user.
   */
  const getTransferDetailsView = (transferList, authorizedActions) => {
    const { status } = transferList;

    if (status === TRANSFER_STATUS.DRAFT) {
      return TRANSFER_DETAILS_VIEW.DRAFT;
    }

    // Once invoice has been generated, the transfer cannot be updated anymore
    if (
      [TRANSFER_STATUS.INVOICED, TRANSFER_STATUS.NON_COMPLIANT, TRANSFER_STATUS.COMPLIANT].includes(
        status,
      )
    ) {
      return TRANSFER_DETAILS_VIEW.DISABLED;
    }

    const { sender, recipient, both } = transferUtils.getUserTransferListAssociations(
      [transferList],
      stores,
    );

    if (both.length > 0) {
      if (status === TRANSFER_STATUS.SENT) {
        if (!canReceiveTransferV2(authorizedActions)) {
          return TRANSFER_DETAILS_VIEW.DISABLED;
        }

        return TRANSFER_DETAILS_VIEW.RECEPTION;
      }

      if (status === TRANSFER_STATUS.REQUESTED) {
        if (!canSendTransfer(authorizedActions)) {
          return TRANSFER_DETAILS_VIEW.DISABLED;
        }

        return TRANSFER_DETAILS_VIEW.DELIVERY;
      }
    }

    if (sender.length > 0) {
      // If already sent, sender cannot update the transfer anymore
      // In that case only delivered quantity should be displayed
      if (status === TRANSFER_STATUS.SENT) {
        return TRANSFER_DETAILS_VIEW.SENDER_DISABLED;
      }

      if (!canSendTransfer(authorizedActions)) {
        return TRANSFER_DETAILS_VIEW.DISABLED;
      }

      // Meaning the transfer is requested, the sender will indicate what quantities will be delivered
      return TRANSFER_DETAILS_VIEW.DELIVERY;
    }

    if (recipient.length > 0) {
      if (!canReceiveTransferV2(authorizedActions)) {
        return TRANSFER_DETAILS_VIEW.DISABLED;
      }

      // Meaning the transfer is sent, the recipient will indicate what quantities have been received
      return TRANSFER_DETAILS_VIEW.RECEPTION;
    }

    // Fallback
    return TRANSFER_DETAILS_VIEW.DISABLED;
  };

  const updateTransferList = async (keyValuePairs) => {
    setTransferList({ ...transferList, ...keyValuePairs });

    if (!!keyValuePairs.senderStoreId) {
      pageLoading();

      const { supplierProducts, recipes } = await getAvailableSupplierProductsAndRecipes(
        keyValuePairs.senderStoreId,
        transferList.sentAt,
      );

      setSupplierProductsToDisplay(supplierProducts);
      setRecipesToDisplay(recipes);

      pageLoaded();
    }
  };

  const updateSPsToDisplay = (supplierProducts) => {
    setSupplierProductsToDisplay(supplierProducts);
    setIsFormDirty(true);
  };

  const openSendModal = () => {
    if (!transferList.senderStoreId || !transferList.recipientStoreId) {
      return;
    }

    const { emailsTo, emailsCC } = allStoresOfClient.reduce(
      (acc, { id, transferEmails }) => {
        if (isEmpty(transferEmails)) {
          return acc;
        }

        // TO Emails
        if (transferList.isSender && id === transferList.recipientStoreId) {
          acc.emailsTo.push(...transferEmails);
        } else if (!transferList.isSender && id === transferList.senderStoreId) {
          acc.emailsTo.push(...transferEmails);
        }

        // CC Emails
        if (transferList.isSender && id === transferList.senderStoreId) {
          acc.emailsCC.push(...transferEmails);
        } else if (!transferList.isSender && id === transferList.recipientStoreId) {
          acc.emailsCC.push(...transferEmails);
        }

        return acc;
      },
      { emailsTo: [], emailsCC: [user.email] },
    );

    setEmailsTo(emailsTo);
    setEmailsCC(emailsCC);

    const config = getSendTransferListModalConfig({
      transferList,
      emailsTo,
      emailsCC,
      setEmailsTo,
      setEmailsCC,
      handleTransferListSend,
    });

    openGenericModal(config);
  };

  const handleTransferListSend = async (emailsTo, emailsCC, transferList) => {
    const transferListPayload = {
      ...transferList,
      isValidating: true,
      emailsTo: (emailsTo || []).map((email) => email.trim()).filter(Boolean),
      emailsCC: (emailsCC || []).map((email) => email.trim()).filter(Boolean),
    };

    if (transferList.status >= TRANSFER_STATUS.REQUESTED) {
      await handleUpdateTransferDeliveryNote(transferListPayload);
      return;
    }

    await handleCreateTransfer(transferListPayload);
  };

  const handleCreateTransfer = async (transferList) => {
    const { supplierProducts, recipes } = formatSPAndRecipesPayloadForCreation(
      supplierProductsToDisplay,
      recipesToDisplay,
    );

    try {
      pageLoading();

      await transfersService.createTransfer({
        transferList,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.CREATION_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.CREATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleCancelInvoiced = async () => {
    pageLoading();

    try {
      await transferListService.cancelInvoiced([transferList.id]);

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.EDIT_SUCCESS'));

      await fetchData();
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleUpdateTransferDeliveryNote = async (transferList) => {
    const { supplierProducts, recipes } = formatSPAndRecipesPayloadForDeliveryNote(
      supplierProductsToDisplay,
      recipesToDisplay,
    );

    try {
      pageLoading();

      await transfersService.updateTransferDeliveryNote({
        transferList,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.EDIT_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  if (isLoading) {
    return <div></div>;
  }

  return (
    <PageContainer>
      <TransfersHeader
        allStoresOfClient={allStoresOfClient}
        datePickerState={datePickerState}
        enableDatePicker={enableDatePicker}
        enableTimePicker={enableTimePicker}
        isFormDirty={isFormDirty}
        isSender={transferList.isSender}
        openSendModal={openSendModal}
        recipesToDisplay={recipesToDisplay}
        supplierProductsToDisplay={supplierProductsToDisplay}
        transferDetailsView={transferDetailsView}
        transferList={transferList}
        updateTransferList={updateTransferList}
      />
      <TransferContent
        cancelInvoiced={handleCancelInvoiced}
        recipesToDisplay={recipesToDisplay}
        setEnableDatePicker={setEnableDatePicker}
        setEnableTimePicker={setEnableTimePicker}
        setRecipesToDisplay={setRecipesToDisplay}
        setSupplierProductsToDisplay={updateSPsToDisplay}
        supplierProductsToDisplay={supplierProductsToDisplay}
        transferDetailsView={transferDetailsView}
        transferList={transferList}
      />
    </PageContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  modal: state.modalReducer,
  stores: getSalesPointStores(state.baseReducer.activeStores),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/stocks/inventories/transfers',
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(TransfersDetails);
